import { Injectable } from '@angular/core';
import { createStore, select, setProp, withProps } from '@ngneat/elf';
import {
  withEntities,
  selectAllEntities,
  setEntities,
  getEntity,
  addEntities,
  updateEntities,
  deleteEntities,
} from '@ngneat/elf-entities';
import { withPagination } from '@ngneat/elf-pagination';
import { persistState } from '@ngneat/elf-persist-state';
import * as localforage from 'localforage';
import * as moment from 'moment';
import { REFRESH_TIME_IN_MINUTES } from 'src/app/shared/configs/global-values.constants';
import { ApiBaseService } from 'src/app/shared/services/api/api-base/api-base.service';
import {
  IStoreProps,
  IListParams,
  IDeleteParams,
  IPaginationState,
  IScrollPosition,
} from 'src/app/talent-litmus-core/common/common.interface';
import { IMiniGame } from './mini-game.interface';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';

// eslint-disable-next-line @typescript-eslint/naming-convention
const STORE_NAME = 'lvl3-mini-game';

const instance = localforage.createInstance({
  driver: localforage.INDEXEDDB,
  name: 'talent-litmus-adminv2',
  version: 1.0,
  storeName: STORE_NAME,
});

const store = createStore(
  { name: STORE_NAME },
  withProps<IStoreProps>({ lastRefreshed: undefined }),
  withProps<IPaginationState>({
    length: 0,
    pageIndex: 0,
    pageSize: 0,
    previousPageIndex: 0,
  }),
  withEntities<IMiniGame, '_id'>({ idKey: '_id' }),
  withProps<IScrollPosition>({ x: 0, y: 0 }),
  withProps<{ selectedId: string }>({ selectedId: null }),
  withProps<{ searchParam: string }>({ searchParam: '' }),
  withPagination(),
);

export const persist = persistState(store, {
  key: STORE_NAME,
  storage: instance,
});

@Injectable()
export class MiniGameCoreService {
  private _loading = false;

  /**
   * @name miniGameList$
   * @description emits stored list of the companies
   */

  miniGameList$ = store.pipe(selectAllEntities());
  paginationState$ = store.pipe(
    select((state) => {
      return state;
    }),
  );
  totalCount: number;

  public get loading(): boolean {
    return this._loading;
  }

  constructor(private _api: ApiBaseService) {
    if (!store.value.lastRefreshed) {
      setTimeout(() => {
        this.refresh();
      }, 400);
    } else {
      this.refresh();
    }
  }

  moveToLastPage(): void {
    let length: number;
    let pageSize: number;
    let previousPageIndex: number;
    this.paginationState$.subscribe((state) => {
      length = state.length;
      pageSize = state.pageSize;
      previousPageIndex = state.previousPageIndex;
    });
    this.pageChangeEvent({
      length,
      pageIndex: Math.ceil(length / pageSize),
      pageSize,
      previousPageIndex,
    });
  }

  pageChangeEvent(event: PageEvent): void {
    store.update((state) => ({
      ...state,
      length: event.length,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      previousPageIndex: event.previousPageIndex,
    }));
  }

  setScrollPosition(scrollPostion: [number, number]): void {
    store.update((state) => ({
      ...state,
      x: scrollPostion[0],
      y: scrollPostion[1],
    }));
  }

  setSelectedId(id: string): void {
    store.update((state) => ({
      ...state,
      selectedId: id,
    }));
  }

  setSearchParam(searchParam: string): void {
    store.update((state) => ({
      ...state,
      searchParam: searchParam,
    }));
  }

  public getMiniGames(params?: IListParams): void {
    this._loading = true;
    this._api
      .get(`mini-games/list`, params || {})
      .subscribe((data: IMiniGameListResponse) => {
        this.totalCount = data.totalCount;
        store.update(
          setEntities(data.list),

          setProp('lastRefreshed', new Date()),
        );
        this._loading = false;
      });
  }

  public addMiniGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.post('mini-games/add', dataInput || {}).pipe(
      tap((data: IMiniGame) => {
        store.update(addEntities(data));
        this._loading = false;
      }),
    );
  }

  public updateMiniGame(dataInput): Observable<any> {
    this._loading = true;
    return this._api.put('mini-games/edit', dataInput || {}).pipe(
      map((data: ArrayBuffer) => data as any),
      tap((data: IMiniGame) => {
        store.update(updateEntities(data['_id'], data));
        this._loading = false;
      }),
    );
  }

  public deleteMiniGame(data: IDeleteParams): Observable<any> {
    this._loading = true;
    return this._api.delete('mini-games', { body: data }).pipe(
      tap(() => {
        store.update(deleteEntities(data['_id']));
        this._loading = false;
      }),
    );
  }

  public getMiniGameInfo(id: string): IMiniGame {
    const miniGame = store.query(getEntity(id));
    return miniGame;
  }

  public resolveMiniGameId(miniGame: string | IMiniGame): string {
    let miniGameId: string | IMiniGame = '';
    if (miniGame) {
      miniGameId = miniGame;
    }
    if (miniGame['_id']) {
      miniGameId = miniGame['_id'];
    }
    return miniGameId as string;
  }

  public refresh(forced?: boolean): void {
    if (
      forced ||
      !store.value.lastRefreshed ||
      moment().diff(moment(store.value.lastRefreshed), 'minutes') >
        REFRESH_TIME_IN_MINUTES
    ) {
      this.getMiniGames();
    }
  }
}

interface IMiniGameListResponse {
  list: IMiniGame[];
  totalCount: number;
}
